import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { Progress } from '@repo/ui';
import { generatePath, useNavigate } from 'react-router-dom';

import ActivationAlert from '@features/ownedParkingManage/components/activation/ActivationAlert';
import ActivationChecklistItem from '@features/ownedParkingManage/components/activation/ActivationChecklistItem';
import type { ActivationReadiness } from '@features/ownedParkingManage/types';
import { ROUTES_TO_ACHIEVE } from '@features/ownedParkingManage/constants/activation';

import type { RouteName } from '@router/routeNames';
import { ROUTE_PATHS } from '@router/paths';

interface Props {
  readiness: ActivationReadiness;
  parkingId: string;
  testId: TestId;
}

export default function ActivationChecklist({ readiness, parkingId, testId }: Props) {
  const navigate = useNavigate();
  const handleClick = (route: RouteName): void => {
    navigate(generatePath(ROUTE_PATHS[route], { id: parkingId }));
  };
  const steps = Object.keys(readiness.steps).length;
  const currentStep = Object.values(readiness.steps).filter(Boolean).length;

  return (
    <>
      <ActivationAlert testId={testId.append('alert')} />

      <div className="bg-warning-medium relative -top-7 z-3 -mb-7 rounded-b-2xl px-4 pt-8 pb-2" data-testid={testId}>
        <Progress
          currentStep={currentStep}
          steps={steps}
          label={tr('owned_parking_manage:parking_statuses.draft.activation.cant_request.info')}
          completedColor="bg-teal"
          upcomingColor="bg-primary"
          compact
          counterOnRight
          testId={testId.append('progress')}
        />

        <div className="mt-3 flex flex-col gap-3">
          <ActivationChecklistItem
            label={tr('owned_parking_manage:parking_statuses.draft.activation.cant_request.not_fulfilled.spots')}
            fulfilled={readiness.steps.spotsFulfilled}
            onClick={() => handleClick(ROUTES_TO_ACHIEVE.spotsFulfilled)}
            testId={testId.append('spotsFulfilled')}
          />
          <ActivationChecklistItem
            label={tr(
              'owned_parking_manage:parking_statuses.draft.activation.cant_request.not_fulfilled.entrance_images'
            )}
            fulfilled={readiness.steps.entranceImagesFulfilled}
            onClick={() => handleClick(ROUTES_TO_ACHIEVE.entranceImagesFulfilled)}
            testId={testId.append('entranceImagesFulfilled')}
          />
          <ActivationChecklistItem
            label={tr('owned_parking_manage:parking_statuses.draft.activation.cant_request.not_fulfilled.settlements')}
            fulfilled={readiness.steps.settlementsFulfilled}
            onClick={() => handleClick(ROUTES_TO_ACHIEVE.settlementsFulfilled)}
            testId={testId.append('settlementsFulfilled')}
          />
        </div>
      </div>
    </>
  );
}
